import { AppointmentCancellationReason } from '@allara-health/source-health-client'
import type { MedicationFeeling } from 'containers/Dashboard/Dashboard/Modals/PrescriptionCheckinModal'

export enum BookingEventSource {
  DASHBOARD_ACTION = 'dashboard_action',
  EMAIL = 'email',
  APP_NAVIGATION = 'app_navigation',
  STICKY_CTA_BUTTON = 'sticky_cta_button',
  INSURANCE_USER_ONBOARDING = 'onboarding',
  MY_HEALTH_APPOINTMENTS = 'my_health_appointments',
  INITIAL_MD_BOOK_DONE = 'intitial_md_book_done',
  SMV_BOOK_DONE = 'smv_book_done',
  UNKNOWN = 'unknown',
  RESCHEDULE = 'reschedule',
  MANAGE = 'booking_flow',
  APPOINTMENT_CANCEL_DONE = 'appointment_cancel_done',
  APPOINTMENT_NOTE = 'appointment_note',
  PRESCRIPTION_CHECKIN = 'prescription_checkin',
}

export enum DashboardActionCTA {
  EXTERNAL_SCHEDULE = 'external_schedule',
}

export type UserStateAndInsuranceCarrier = { state: string; carrier: string }

export type GenericAppointmentEventProps = {
  appointment_type_name: string // e.g. 'Initial Medical Visit'
  patient_action_id: string | null // null if we don't know/have the action that drove the patient's behavior
  patient_action_definition: string | null // same as above
  source: BookingEventSource
}

export type NetNewAppointmentEventProps = GenericAppointmentEventProps & {
  appointment_id: null
  appointment_time: null
  is_reschedule: false
}

export type ExistingAppointmentEventProps = GenericAppointmentEventProps & {
  appointment_id: string
  appointment_time: string
  is_reschedule: boolean // false if the user just booked the appointment
}

export type NewOrExistingAppointmentEventProps =
  | NetNewAppointmentEventProps
  | ExistingAppointmentEventProps

export type AppointmentBookDoneProps = NewOrExistingAppointmentEventProps &
  UserStateAndInsuranceCarrier & {
    days_since_nearest_slot?: number
    penalty_score?: number
  }

export type AppointmentBookStartEventProps = Omit<
  NewOrExistingAppointmentEventProps,
  'appointment_time'
>

export type AppointmentBookStateSelectedEventProps = Omit<
  NewOrExistingAppointmentEventProps,
  'appointment_time'
> & {
  state_selected: string // e.g. 'CA'
  state_on_file: string // e.g. 'CA'
  is_valid_selection: boolean
}

export type AppointmentBookAvailabilityClickEventProps = Omit<
  NewOrExistingAppointmentEventProps,
  'appointment_time'
> & {
  slot_time: string
  available_providers_cnt: number
  available_providers: string[]
}

const UserIdPrefix = 'usr' as const

export type ProviderId = `${typeof UserIdPrefix}_${string}`

export type AppointmentBookAvailabilityShowEventProps = Omit<
  NewOrExistingAppointmentEventProps,
  'appointment_time'
> &
  UserStateAndInsuranceCarrier & {
    nearest_slot_time: string
    slots_date: string
    available_slots_cnt: number
    current_month_available_slots_cnt: number
    current_month_available_slot_dates_cnt: number
    available_slot_times: string[]
    available_providers_cnt?: number
    available_providers?: ProviderId[]
    available_providers_and_slots?: string
    available_providers_and_slot_counts?: string
    is_first_availability?: boolean
    days_to_nearest_slot?: number
    no_penalty_slots_cnt?: number
    medium_penalty_slots_cnt?: number
    high_penalty_slots_cnt?: number
    shown_slots_cnt?: number
    hidden_cta_slots_cnt?: number
    withheld_slots_cnt?: number
  }

export type AppointmentBookSlotSelectedEventProps = Omit<
  NewOrExistingAppointmentEventProps,
  'appointment_time'
> &
  UserStateAndInsuranceCarrier & {
    slot_time: string
    provider_id: string
    days_since_nearest_slot?: number
    penalty_score?: number
  }

export type AppointmentModifyEventProps = Omit<
  ExistingAppointmentEventProps,
  'is_reschedule' | 'patient_action_id' | 'patient_action_definition' | 'source'
>

export type AppointmentCancelModalClickEventProps = AppointmentModifyEventProps & {
  modal_header: string
  modal_content: string
  cta: 'cancel' | 'close' | 'reschedule'
}

export type AppointmentCancelReasonModalClickEventProps = AppointmentModifyEventProps & {
  code?: AppointmentCancellationReason
  cta: 'cancel' | 'close' | 'dont_cancel'
}

export type AppointmentJoinStartEventProps = Omit<
  ExistingAppointmentEventProps,
  'is_reschedule'
>

export type MessageThreadOpenEventProps = {
  thread_id: string
  is_unread: boolean
}

export type MessageSendDoneEventProps = {
  attachments_num: number
  message_id: string
  is_new_message: boolean
  selected_topic: string | null
}

export type MessageAttachStartEventProps = {
  thread_id: string
}

export type AppointmentNoteOpenEventProps = {
  appointment_note_id: string
  appointment_id: string
  appointment_type_name: string
  source: string
}

export type LabResultDownloadDoneEventProps = {
  lab_title: string
  lab_id: string
  lab_type: string
}

export type DashboardActionClickEventProps = {
  patient_action_id: string
  patient_action_definition: string
  cta?: DashboardActionCTA
}

export type NavigationSource = 'email' | 'app_navigation'

export type CommunicationsPreferencesUpdateDoneProps = {
  receives_medical_appointment_emails: boolean
  receives_nutrition_appointment_emails: boolean
  receives_care_journey_emails: boolean
  previous_medical_appointment_emails: boolean
  previous_nutrition_appointment_emails: boolean
  previous_care_journey_emails: boolean
  has_unsubscribed_from_all: boolean
  source: NavigationSource
}

export type CarePlanCheckInStartEventProps = {
  source: BookingEventSource.PRESCRIPTION_CHECKIN
  resource_id: string
  checkin_type: 'prescription'
}

export type CarePlanCheckInDoneEventProps = CarePlanCheckInStartEventProps & {
  feeling_scale_selected: MedicationFeeling
  free_text_note: string
}
// Lab Scheduling

export enum LabSchedulingSource {
  DASHBOARD_ACTION = 'dashboard_action',
  EMAIL = 'email',
  LAB_CANCEL_DONE = 'lab_cancel_done',
  RESCHEDULE = 'reschedule',
  APPOINTMENT_NOTE = 'appointment_note',
}

export enum LabSchedulingFilter {
  DISTANCE_CLOSEST = 'distance_closest',
  APPOINTMENT_FIRST_AVAILABLE = 'appointment_first_available',
}

export enum LabProvider {
  LABCORP = 'labcorp',
  QUEST = 'quest',
}

export enum LabSchedulingCTA {
  EXTERNAL = 'booked_external',
}

type BaseLabSchedulingProps = {
  source: LabSchedulingSource
  patient_action_id?: string
  is_reschedule: boolean
  lab_provider: LabProvider
  lab_appointment_id?: string
}

export type LabAppointmentDetailsClickProps = {
  lab_location: string
  lab_code: string
  lab_appointment_id: string
  lab_appointment_time: string
  lab_provider: LabProvider
}

export type DashboardActionClickProps = {
  patient_action_id: string
  patient_action_definition: string
  cta: LabSchedulingCTA
}

export type LabScheduleStartProps = {
  patient_action_definition?: string //ex: book-initial-rd, book-followup-md, ...
} & Omit<BaseLabSchedulingProps, 'lab_provider'>

export type LabScheduleProviderSelectedProps = Omit<
  BaseLabSchedulingProps,
  'patient_action_id'
>

export type LabScheduleZipSelectedProps = {
  address: string
} & Omit<BaseLabSchedulingProps, 'patient_action_id'>

export type LabScheduleAvailabilityShowProps = {
  nearest_slot_time: string //In UTC
  slots_date: string //In UTC format YYYY-MM-DD
  total_available_slots_cnt: number
  total_available_slot_dates_cnt: number
  available_slots_cnt: number
  available_slot_times: string[]
  available_labs_cnt: number //Number of locations
  available_labs: string[] //The location addresses
  available_labs_codes: string[] //The location codes
  filter_applied: LabSchedulingFilter
  filter_location_radius_miles: string
  is_first_availability: boolean
  days_to_nearest_slot: number
} & BaseLabSchedulingProps

export type LabScheduleAvailabilityMoreTimesClickProps = {
  slots_date: string
  lab_location: string
  lab_code: string
  available_slots_times: string[]
  available_slots_cnt: number
  filter_applied: LabSchedulingFilter
} & Omit<BaseLabSchedulingProps, 'source' | 'patient_action_id'>

export type LabScheduleSlotSelectedProps = {
  lab_appointment_time: string // Timestamp UTC
  lab_location: string // Lab address
  lab_code: string
  days_since_nearest_slot: number // Number of days between the nearest slot from first availability and the selected slot
} & BaseLabSchedulingProps

export type LabScheduleDoneProps = {
  lab_location: string
  lab_code: string

  lab_appointment_time: string
  days_since_nearest_slot: number
} & BaseLabSchedulingProps

type LabModifyProps = {
  lab_location: string
  lab_code: string

  lab_appointment_time: string
  lab_provider: LabProvider
  lab_appointment_id: string
}

export type LabModifyStartProps = LabModifyProps
export type LabCancelStartProps = LabModifyProps
export type LabCancelDoneProps = LabModifyProps

//
